import { memo } from 'react'

import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import Page from 'app/layout/Page'
import { DynamicBanner } from 'app/lib/components/DynamicBanner'
import { NoAccess } from 'app/lib/components/NoAccess'
import { NoResults } from 'app/lib/components/NoResults'
import { SearchBar } from 'app/lib/components/SearchBar'
import { useListOrganizationsQuery } from 'app/redux/scribeApi'
import { Manage } from 'assets/images'

import { Table } from './Table'

export const EligibilityRecordListPage = memo(() => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: organizationsResp, isFetching: isFetchingOrgs } = useListOrganizationsQuery({
    limit: 1,
  })
  const { data: organizations = [] } = organizationsResp || {}
  const hasNoOrganization = !isFetchingOrgs && organizations?.length === 0

  const querySearchValue = searchParams.get('search')

  const handleMemberSearch = (search: string) => {
    const params = Object.fromEntries(searchParams.entries())

    // Remove pagination params
    const { offset, cursor, ...otherParams } = params

    if (search) setSearchParams({ ...otherParams, search })
    else setSearchParams({})
  }

  return (
    <Page isLoading={isFetchingOrgs}>
      <DynamicBanner pageKey={'eligibility-record-search'} />
      {hasNoOrganization ? (
        <NoAccess />
      ) : (
        <>
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={4}>
            <Typography variant="h1" display="flex" alignItems="top">
              {t('eligibilityRecordListPage.pageTitleAll')}
              <Tooltip
                title={t('eligibilityRecordListPage.titleAllTooltip')}
                componentsProps={{ tooltip: { sx: { maxWidth: 'none' } } }}
                arrow
              >
                <HelpOutlineIcon sx={{ fontSize: 14, ml: 0.5, mt: 1, cursor: 'pointer' }} />
              </Tooltip>
            </Typography>
            <Box sx={{ minWidth: '500px' }}>
              <SearchBar
                inputLabel={t('eligibilityRecordListPage.searchForAMember')}
                onSearch={handleMemberSearch}
                search={querySearchValue || ''}
                minSearchTermLength={3}
                data-testid={'search-member-input'}
              />
            </Box>
          </Stack>
          <Box
            sx={{
              mb: 3,
              mt: 4,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {querySearchValue ? (
              <Table />
            ) : (
              <NoResults
                title={t('eligibilityRecordListPage.eligibilityRecordsTable.searchMember')}
                subtitle={t('eligibilityRecordListPage.eligibilityRecordsTable.typeInTheSearch')}
                img={<Manage title="no_member_found" />}
              />
            )}
          </Box>
        </>
      )}
    </Page>
  )
})
